import { useEffect, useState, useRef } from "react";
import {
  FiX,
  FiSend,
  FiAlertCircle,
  FiCheckCircle,
  FiChevronDown,
  FiSearch,
  FiInfo,
} from "react-icons/fi";
import { ImSpinner } from "react-icons/im";
import ButtonLoading from "./ButtonLoading";
import { userRequest } from "../utils/requestMethods";
import SendOTP from "./SendOTP";
import { formatCurrency } from "../utils/currencyUtils";

const TRANSACTION_FEE = 50;
const VAT_RATE = 0.075;
const VAT = TRANSACTION_FEE * VAT_RATE; // 3.75

const BankSelect = ({ banks, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const selected = banks.find((b) => b.bank_code === value);
  const filtered = banks.filter((b) => b.name.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const handleSelect = (bank) => {
    onChange(bank);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:outline-none disabled:opacity-50"
      >
        <span className={selected ? "text-gray-900" : "text-gray-400"}>
          {selected ? selected.name : "Select bank"}
        </span>
        <FiChevronDown
          className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <FiSearch className="text-gray-400 shrink-0 text-sm" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search banks..."
              className="flex-1 text-sm bg-transparent border-none outline-none text-gray-700 placeholder-gray-400"
            />
          </div>
          <ul className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-gray-400 text-center">No banks found</li>
            ) : (
              filtered.map((bank) => (
                <li
                  key={bank.bank_code}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(bank);
                  }}
                  className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-indigo-50 ${
                    bank.bank_code === value
                      ? "bg-indigo-50 text-indigo-600 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {bank.name}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const TransferModal = ({ closeModal, school, balance, onSuccess }) => {
  const multiFundEnabled = !!school?.settings?.finance?.multi_fund_enabled;
  const [funds, setFunds] = useState([]);
  const [selectedFundId, setSelectedFundId] = useState("");
  const [banks, setBanks] = useState([]);
  const [amount, setAmount] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [narration, setNarration] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  const selectedFund = funds.find((f) => f._id === selectedFundId);
  const activeAvailableBalance = multiFundEnabled && selectedFund?.balance
    ? parseFloat(selectedFund.balance.availableBalance) || 0
    : parseFloat(balance?.availableBalance) || 0;

  const amountNum = parseFloat(amount) || 0;
  const totalDeduction = amountNum + TRANSACTION_FEE + VAT;
  const availableBalance = activeAvailableBalance;
  const hasSufficientBalance = amountNum > 0 && availableBalance >= totalDeduction;
  const showBreakdown = amountNum > 0;

  useEffect(() => {
    const fetchBanksAndCategories = async () => {
      try {
        const [banksRes, categoriesRes, fundsRes] = await Promise.all([
          userRequest.get("/banking/withdrawal-banks"),
          school?._id
            ? userRequest.get(`/schools/${school._id}/finance/expense-categories`)
            : Promise.resolve(null),
          school?._id && multiFundEnabled
            ? userRequest.get(`/schools/${school._id}/funds`)
            : Promise.resolve(null),
        ]);
        const bankList = banksRes?.data?.bank_codes || [];
        const seen = new Set();
        const uniqueBanks = bankList.filter((b) => {
          if (seen.has(b.bank_code)) return false;
          seen.add(b.bank_code);
          return true;
        });
        setBanks(uniqueBanks);

        const rawCat = categoriesRes?.data?.data || categoriesRes?.data;
        if (rawCat) {
          const systemCats = Array.isArray(rawCat.system) ? rawCat.system : [];
          const customCats = Array.isArray(rawCat.custom) ? rawCat.custom : [];
          const combined = [...systemCats, ...customCats];
          if (combined.length > 0) {
            setCategories(combined);
          } else if (Array.isArray(rawCat.categories)) {
            setCategories(rawCat.categories);
          } else if (Array.isArray(rawCat)) {
            setCategories(rawCat);
          }
        }

        const fundList = fundsRes?.data?.data?.funds || fundsRes?.data?.funds || [];
        if (Array.isArray(fundList) && fundList.length > 0) {
          setFunds(fundList);
          const defaultFund = fundList.find((f) => f.is_default) || fundList[0];
          setSelectedFundId(defaultFund?._id || "");
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchBanksAndCategories();
  }, [school?._id, multiFundEnabled]);

  const resolveAccount = async (code, number) => {
    if (!code || !number) return;
    setResolving(true);
    setAccountName("");
    setError("");
    try {
      const { data } = await userRequest.post("banking/validate-account", {
        accounts: [{ bankCode: code, accountNumber: number }],
      });
      const result = data?.result?.[0];
      setAccountName(result?.beneficiaryName || "");
      if (!result?.beneficiaryName) setError("Could not resolve account name.");
    } catch {
      setError("Error, unable to resolve account!");
    } finally {
      setResolving(false);
    }
  };

  const handleBankSelect = (bank) => {
    setBankCode(bank.bank_code);
    resolveAccount(bank.bank_code, accountNumber);
  };

  const handleAccountNumberBlur = () => {
    resolveAccount(bankCode, accountNumber);
  };

  const saveHandler = async (e) => {
    e.preventDefault();
    if (!amount || !accountNumber || !bankCode || !accountName || !category || !otp) {
      setError("Please fill all the fields.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await userRequest.post("banking/transfers", {
        schoolId: school?._id,
        fundId: selectedFundId || undefined,
        fund_id: selectedFundId || undefined,
        amount: Number(amount),
        destinationBankCode: bankCode,
        destinationAccountNumber: accountNumber,
        destinationAccountName: accountName,
        narration: narration || "School Transfer",
        category,
        otp,
      });
      setSuccess(true);
      onSuccess?.();
      setTimeout(closeModal, 2500);
    } catch (err) {
      setError(err?.response?.data?.message || "Transfer failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal} />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto z-10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-xl">
              <FiSend className="text-indigo-600 text-lg" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Make a Transfer</h3>
          </div>
          <button
            onClick={closeModal}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
              <FiCheckCircle className="mt-0.5 shrink-0" />
              <span>
                <b>Success!</b> Money is on its way.
              </span>
            </div>
          )}

          <form onSubmit={saveHandler} className="space-y-4">
            <SendOTP school={school} />

            {multiFundEnabled && funds.length > 0 && (
              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">
                  Source Fund <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedFundId}
                  onChange={(e) => setSelectedFundId(e.target.value)}
                  className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none font-medium"
                >
                  {funds.map((f) => (
                    <option key={f._id} value={f._id}>
                      {f.name} {f.is_default ? "(Default)" : ""} — ₦
                      {parseFloat(f.balance?.availableBalance || 0).toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700">Amount (₦)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 10000"
                className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
              />
              {showBreakdown && (
                <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Transfer amount</span>
                    <span>{formatCurrency(amountNum)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-1">
                      Transaction fee
                      <FiInfo className="text-gray-400 text-xs" title="Flat fee per transfer" />
                    </span>
                    <span>{formatCurrency(TRANSACTION_FEE)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-1">
                      VAT (7.5%)
                      <FiInfo
                        className="text-gray-400 text-xs"
                        title="7.5% VAT on transaction fee"
                      />
                    </span>
                    <span>{formatCurrency(VAT)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold text-gray-800">
                    <span>Total deducted</span>
                    <span>{formatCurrency(totalDeduction)}</span>
                  </div>
                  <div
                    className={`flex justify-between text-xs pt-1 ${hasSufficientBalance ? "text-green-600" : "text-red-500"}`}
                  >
                    <span>Available balance</span>
                    <span>{formatCurrency(availableBalance)}</span>
                  </div>
                  {!hasSufficientBalance && (
                    <div className="flex items-center gap-1.5 text-xs text-red-500 pt-1">
                      <FiAlertCircle className="shrink-0" />
                      Insufficient balance for this transfer
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700">
                Account Number
              </label>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                onBlur={handleAccountNumberBlur}
                placeholder="Enter 10-digit account number"
                className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700">Bank Name</label>
              <BankSelect
                banks={banks}
                value={bankCode}
                onChange={handleBankSelect}
                disabled={banks.length === 0}
              />
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700">
                Account Name
                {resolving && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs text-indigo-500 font-normal">
                    <ImSpinner className="animate-spin" /> Verifying...
                  </span>
                )}
              </label>
              <input
                type="text"
                value={accountName}
                readOnly
                placeholder="Auto-filled after verification"
                className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm text-gray-500 cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700">Narration</label>
              <input
                type="text"
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                placeholder="e.g. Food vendor payment"
                className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
              />
              <div className="flex gap-1.5 mt-2 overflow-x-auto pb-1 scrollbar-hide">
                {[
                  "Staff salary",
                  "Food vendor payment",
                  "Maintenance & repairs",
                  "Utility bills",
                  "Security services",
                  "Cleaning services",
                  "Office supplies",
                  "Transport allowance",
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => setNarration(suggestion)}
                    className={`shrink-0 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      narration === suggestion
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-gray-50 text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600"
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-medium text-gray-700">OTP</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter generated OTP"
                className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
              />
            </div>

            {loading ? (
              <ButtonLoading styles="w-full text-white bg-indigo-400 font-medium rounded-full text-sm px-5 py-2.5 text-center" />
            ) : (
              <button
                type="submit"
                disabled={resolving || !accountName || (showBreakdown && !hasSufficientBalance)}
                className="w-full inline-flex items-center justify-center gap-2 text-white bg-indigo-600 hover:bg-indigo-700 font-medium rounded-full text-sm px-5 py-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiSend className="w-4 h-4" />
                Send Funds
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferModal;
